import React, { useEffect, useMemo, useRef, useState } from 'react'
import {
  Button,
  Tooltip,
  TOOLTIP_DEFAULT_CONTENT,
  TOOLTIP_PLACEMENT_LABELS,
  TOOLTIP_PLACEMENTS,
  type TooltipPlacement,
} from '@components/ui'
import UiPropertiesPanel, {
  type UiPropertySection,
} from '../components/UiPropertiesPanel'
import UiPreviewCanvas from '../components/UiPreviewCanvas'
import './TooltipShowcase.css'

const TooltipShowcase: React.FC = () => {
  const [content, setContent] = useState(TOOLTIP_DEFAULT_CONTENT)
  const [highlightedPlacement, setHighlightedPlacement] =
    useState<TooltipPlacement>('top')
  const [darkMode, setDarkMode] = useState(false)
  const cardRefs = useRef<
    Partial<Record<TooltipPlacement, HTMLDivElement | null>>
  >({})

  useEffect(() => {
    cardRefs.current[highlightedPlacement]?.scrollIntoView({
      block: 'nearest',
      behavior: 'smooth',
    })
  }, [highlightedPlacement])

  const propertySections: UiPropertySection[] = useMemo(
    () => [
      {
        title: 'Content',
        fields: [
          {
            id: 'tooltip-highlight-placement',
            label: 'Highlighted placement',
            control: (
              <select
                id="tooltip-highlight-placement"
                className="ui-field-control"
                value={highlightedPlacement}
                onChange={(event) =>
                  setHighlightedPlacement(
                    event.target.value as TooltipPlacement,
                  )
                }
              >
                {TOOLTIP_PLACEMENTS.map((option) => (
                  <option key={option} value={option}>
                    {TOOLTIP_PLACEMENT_LABELS[option]}
                  </option>
                ))}
              </select>
            ),
          },
          {
            id: 'tooltip-content',
            label: 'Description',
            control: (
              <input
                id="tooltip-content"
                className="ui-field-control"
                type="text"
                value={content}
                onChange={(event) => setContent(event.target.value)}
              />
            ),
          },
          {
            id: 'tooltip-dark-mode',
            label: 'Dark mode',
            control: (
              <label className="ui-field-checkbox">
                <input
                  id="tooltip-dark-mode"
                  type="checkbox"
                  checked={darkMode}
                  onChange={(event) => setDarkMode(event.target.checked)}
                />
                <span>Enabled</span>
              </label>
            ),
          },
        ],
      },
    ],
    [content, darkMode, highlightedPlacement],
  )

  return (
    <>
      <UiPreviewCanvas
        title="Tooltip"
        description="Compact contextual label with directional arrow for supplementary information on hover or focus."
        stageClassName="ui-preview__stage--wide tooltip-showcase__stage-wrap"
      >
        <div
          className={`tooltip-showcase__sheet${
            darkMode ? ' tooltip-showcase__sheet--dark' : ''
          }`}
        >
          <section
            className={`ui-showcase-live${
              darkMode ? ' ui-showcase-live--dark' : ''
            }`}
          >
            <h3 className="ui-showcase-live__title">Live preview</h3>
            <p className="ui-showcase-live__note">
              All placements at once — hover or focus each trigger to compare
              positioning.
            </p>
            <div className="tooltip-showcase__live-grid">
              {TOOLTIP_PLACEMENTS.map((option) => (
                <div
                  key={option}
                  ref={(element) => {
                    cardRefs.current[option] = element
                  }}
                  className={`tooltip-showcase__live-card${
                    option === highlightedPlacement
                      ? ' tooltip-showcase__live-card--selected'
                      : ''
                  }${darkMode ? ' tooltip-showcase__live-card--dark' : ''}`}
                >
                  <span className="tooltip-showcase__live-card-label">
                    {TOOLTIP_PLACEMENT_LABELS[option]}
                  </span>
                  <Tooltip
                    content={content}
                    placement={option}
                    darkMode={darkMode}
                  >
                    <Button variant="secondary" size="sm" darkMode={darkMode}>
                      Trigger
                    </Button>
                  </Tooltip>
                </div>
              ))}
            </div>
          </section>
        </div>
      </UiPreviewCanvas>

      <UiPropertiesPanel componentName="Tooltip" sections={propertySections} />
    </>
  )
}

export default TooltipShowcase
